/**
 * Authentication Utilities for HR Platform Frontend
 *
 * This module provides authentication-related utilities for managing
 * JWT tokens, session state, and auth-related operations.
 */

// ============================================
// Storage Keys
// ============================================

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
} as const;

// ============================================
// Token Storage Functions
// ============================================

/**
 * Get the access token from localStorage
 * Returns null if running on server or token doesn't exist
 */
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
}

/**
 * Get the refresh token from localStorage
 * Note: In production, refresh tokens should be in HttpOnly cookies
 */
export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
}

/**
 * Store both access and refresh tokens
 */
export function setTokens(accessToken: string, refreshToken?: string): void {
  if (typeof window === 'undefined') return;

  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);

  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }
}

/**
 * Set only the access token
 */
export function setAccessToken(token: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  }
}

/**
 * Clear all authentication tokens from storage
 */
export function clearTokens(): void {
  if (typeof window === 'undefined') return;

  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
}

// ============================================
// JWT Token Utilities
// ============================================

/**
 * JWT payload interface (standard claims)
 */
export interface JwtPayload {
  sub: string; // Subject (user ID)
  email?: string;
  tenantId?: string;
  role?: string;
  exp: number; // Expiration time (Unix timestamp)
  iat: number; // Issued at time (Unix timestamp)
  [key: string]: unknown; // Allow additional claims
}

/**
 * Decode a JWT token payload without verification
 * Note: This only decodes - it does NOT verify the signature
 *
 * @param token - The JWT token to decode
 * @returns The decoded payload or null if invalid
 */
export function getTokenPayload(token: string): JwtPayload | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    if (!payload) {
      return null;
    }
    // Handle base64url encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * Includes a buffer of 30 seconds to account for clock skew
 *
 * @param token - The JWT token to check
 * @param bufferSeconds - Buffer time before actual expiration (default: 30s)
 * @returns true if the token is expired or invalid
 */
export function isTokenExpired(token: string, bufferSeconds: number = 30): boolean {
  const payload = getTokenPayload(token);

  if (!payload || !payload.exp) {
    return true; // Treat invalid tokens as expired
  }

  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + bufferSeconds;
}

/**
 * Get the time until token expiration in seconds
 * Returns 0 if token is already expired or invalid
 */
export function getTokenExpirationTime(token: string): number {
  const payload = getTokenPayload(token);

  if (!payload || !payload.exp) {
    return 0;
  }

  const now = Math.floor(Date.now() / 1000);
  const remaining = payload.exp - now;

  return Math.max(0, remaining);
}

// ============================================
// Token Refresh
// ============================================

// Promise to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh the access token using the BFF endpoint
 * Prevents multiple simultaneous refresh attempts
 *
 * @returns The new access token or null if refresh failed
 */
export async function refreshAccessToken(): Promise<string | null> {
  // If already refreshing, wait for that to complete
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include', // Include HttpOnly cookies
      });

      if (!response.ok) {
        clearTokens();
        return null;
      }

      const data = await response.json();
      const newToken = data.accessToken;

      if (newToken) {
        setAccessToken(newToken);
      }

      return newToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearTokens();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ============================================
// Auth Wrapper
// ============================================

/**
 * Options for the withAuth wrapper
 */
export interface WithAuthOptions {
  /** Automatically retry with refreshed token on 401 */
  autoRefresh?: boolean;
  /** Additional headers to include */
  headers?: Record<string, string>;
}

/**
 * Wrapper that adds authentication to fetch requests
 * Automatically handles token refresh on 401 responses
 *
 * @param fetchFn - The fetch function to wrap (or provide options for fetch)
 * @param options - Configuration options
 * @returns The fetch response
 */
export async function withAuth<T>(
  fetchFn: () => Promise<Response>,
  options: WithAuthOptions = {}
): Promise<T> {
  const { autoRefresh = true } = options;

  const token = getAccessToken();

  // Make the initial request
  const response = await fetchFn();

  // Handle 401 - attempt token refresh
  if (response.status === 401 && autoRefresh && token) {
    const newToken = await refreshAccessToken();

    if (newToken) {
      // Retry the request with new token
      const retryResponse = await fetchFn();

      if (!retryResponse.ok) {
        throw new Error(`Request failed: ${retryResponse.status}`);
      }

      return retryResponse.json();
    }
  }

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Create fetch options with authorization header
 */
export function createAuthHeaders(
  additionalHeaders?: Record<string, string>
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

// ============================================
// Logout
// ============================================

/**
 * Logout the user - clears tokens and redirects to login
 *
 * @param redirectUrl - URL to redirect to after logout (default: /login)
 */
export async function logout(redirectUrl: string = '/login'): Promise<void> {
  try {
    // Call logout endpoint to invalidate server-side session
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    // Continue with local cleanup even if server logout fails
    console.error('Server logout failed:', error);
  }

  // Clear local tokens
  clearTokens();

  // Clear persisted stores (hr-* keys in localStorage)
  if (typeof window !== 'undefined') {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('hr-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  // Redirect to login page
  if (typeof window !== 'undefined') {
    window.location.href = redirectUrl;
  }
}

// ============================================
// Session Status
// ============================================

/**
 * Check if the user appears to be authenticated
 * Note: This only checks for token presence, not validity
 */
export function hasAuthTokens(): boolean {
  return getAccessToken() !== null;
}

/**
 * Check if the user has valid (non-expired) auth tokens
 */
export function hasValidAuthTokens(): boolean {
  const token = getAccessToken();
  return token !== null && !isTokenExpired(token);
}

/**
 * Get the current user ID from the access token
 */
export function getCurrentUserId(): string | null {
  const token = getAccessToken();
  if (!token) return null;

  const payload = getTokenPayload(token);
  return payload?.sub || null;
}

/**
 * Get the current tenant ID from the access token
 */
export function getCurrentTenantId(): string | null {
  const token = getAccessToken();
  if (!token) return null;

  const payload = getTokenPayload(token);
  return payload?.tenantId || null;
}
